import { useEffect, useRef } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { gaPush } from '@/configuration/setup/googleAnalytics';
import InternalErrorState from '@/features/app/InternalErrorState';
import { DetailPageTab, sendMessage, VIEW_ASSET_DETAIL } from '@/features/app/MessageHandler';
import CsvButtonContainer from './CsvButtonContainer';
import DataTabsSettingsDialogContainer from './DataTabsSettingsDialogContainer';
import FilterComponent, { Criticality, SyncStatus } from './FilterComponent';
import PaginationControlsComponent from './PaginationControlsComponent';
import useFetchVehiclesStatusData from './useFetchVehiclesStatusData';
import VehicleDataTabsComponent from './VehicleDataTabsComponent';

const getCriticalityFilterOptions = (intl: IntlShape, selectedItems: Criticality[]) => [
    {
        id: Criticality.CRITICAL,
        label: intl.formatMessage({ id: 'intl-msg:vehicle_status.filters.criticality.critical' }),
        icon: <span className="rioglyph rioglyph-error-sign text-size-16 text-color-danger" />,
        selected: selectedItems.length === 0 || selectedItems.includes(Criticality.CRITICAL),
    },
    {
        id: Criticality.WARNING,
        label: intl.formatMessage({ id: 'intl-msg:vehicle_status.filters.criticality.warning' }),
        icon: <span className="rioglyph rioglyph-warning-sign text-size-16 text-color-warning" />,
        selected: selectedItems.length === 0 || selectedItems.includes(Criticality.WARNING),
    },
    {
        id: Criticality.OK,
        label: intl.formatMessage({ id: 'intl-msg:vehicle_status.filters.criticality.ok' }),
        icon: <span className="rioglyph rioglyph-ok text-size-16 text-normal" />,
        selected: selectedItems.length === 0 || selectedItems.includes(Criticality.OK),
    },
];

const getSyncStatusFilterOptions = (intl: IntlShape, selectedItems: SyncStatus[]) => [
    {
        id: SyncStatus.SYNCED,
        label: intl.formatMessage({ id: 'intl-msg:vehicle_status.filters.sync_status.synced' }),
        icon: <span className="rioglyph rioglyph-refresh text-size-16 text-color-success" />,
        selected: selectedItems.length === 0 || selectedItems.includes(SyncStatus.SYNCED),
    },
    {
        id: SyncStatus.RECENTLY_SYNCED,
        label: intl.formatMessage({ id: 'intl-msg:vehicle_status.filters.sync_status.recently_synced' }),
        icon: <span className="rioglyph rioglyph-refresh text-size-16 text-color-warning" />,
        selected: selectedItems.length === 0 || selectedItems.includes(SyncStatus.RECENTLY_SYNCED),
    },
    {
        id: SyncStatus.NOT_SYNCED,
        label: intl.formatMessage({ id: 'intl-msg:vehicle_status.filters.sync_status.not_synced' }),
        icon: <span className="rioglyph rioglyph-refresh text-size-16 text-color-danger" />,
        selected: selectedItems.length === 0 || selectedItems.includes(SyncStatus.NOT_SYNCED),
    },
];

const VehicleDataTabsContainer = () => {
    const {
        isLoading,
        hasErrors,
        vehiclesStatusData,
        pageSize,
        pageNumber,
        totalPages,
        isCsvExportAllowed,
        triggerReload,
        changeCriticalityStatus,
        changeSyncStatus,
        changePageNumber,
        changePageSize,
        changeSelectedDataTabSections,
        selectedDataTabsSections,
        criticalityStatus,
        syncStatus,
        setSelectedAssetId,
        highlightAssetId,
        clearHighlight,
    } = useFetchVehiclesStatusData();
    const intl = useIntl();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const redirectToDetailPage = (assetId: string, detailPageTab: DetailPageTab = DetailPageTab.GENERAL) => {
        sendMessage({
            type: VIEW_ASSET_DETAIL,
            payload: {
                assetId,
                detailPageTab,
            },
        });
        timeoutRef.current = setTimeout(() => {
            // We need to set the asset one second later to avoid
            // highlighting a moment before navigating to detail view
            setSelectedAssetId(assetId);
        }, 1000);
    };

    if (hasErrors) {
        return <InternalErrorState reloadTriggerFunction={triggerReload} additionalClassName="margin-top-20" />;
    }
    return (
        <div className="display-flex flex-column margin-top-20" data-testid="vehicles-details-container">
            <div className="display-flex justify-content-between">
                <div className="display-flex flex-row gap-20">
                    <FilterComponent
                        isLoading={isLoading}
                        title={intl.formatMessage({ id: 'intl-msg:vehicle_status.filters.criticality.title' })}
                        placeholder=""
                        filterOptions={getCriticalityFilterOptions(intl, criticalityStatus)}
                        onSelect={(selectedOptionsArray: any[]) => {
                            const appliedFilters = `appliedCriticalityFilters: ${selectedOptionsArray.join(',')}`;
                            gaPush({
                                event: 'filter_criticality',
                                parameters: {
                                    element_name: 'VehicleDataTabsContainer',
                                    component_name: 'Select',
                                    trigger: 'click',
                                    element_state: appliedFilters,
                                },
                            });
                            changeCriticalityStatus(selectedOptionsArray);
                        }}
                    />
                    <FilterComponent
                        isLoading={isLoading}
                        title={intl.formatMessage({ id: 'intl-msg:vehicle_status.filters.sync_status.title' })}
                        placeholder=""
                        tooltip={intl.formatMessage({
                            id: 'intl-msg:vehicle_status.filters.sync_status.tooltip.message',
                        })}
                        filterOptions={getSyncStatusFilterOptions(intl, syncStatus)}
                        onSelect={(selectedOptionsArray: any[]) => {
                            const appliedFilters = `appliedSyncFilters: ${selectedOptionsArray.join(',')}`;
                            gaPush({
                                event: 'filter_sync',
                                parameters: {
                                    element_name: 'VehicleDataTabsContainer',
                                    component_name: 'Select',
                                    trigger: 'click',
                                    element_state: appliedFilters,
                                },
                            });
                            changeSyncStatus(selectedOptionsArray);
                        }}
                    />
                </div>
                <div className="display-flex flex-row gap-5">
                    <DataTabsSettingsDialogContainer
                        savedDataTabSettings={selectedDataTabsSections}
                        onSaveDataTabSettingsDialog={changeSelectedDataTabSections}
                    />
                    {isCsvExportAllowed && (
                        <CsvButtonContainer
                            criticalityStatus={criticalityStatus}
                            syncStatus={syncStatus}
                            dataTabsSettings={selectedDataTabsSections}
                        />
                    )}
                </div>
            </div>
            <VehicleDataTabsComponent
                isLoading={isLoading}
                vehiclesStatusData={vehiclesStatusData}
                onClickVehicleNameAndVinAndLicensePlate={(asset_id: string) =>
                    redirectToDetailPage(asset_id, DetailPageTab.GENERAL)
                }
                onClickCustomerName={(asset_id: string) => redirectToDetailPage(asset_id, DetailPageTab.VEHICLE_INFO)}
                onClickVehicleInfoSection={(asset_id: string) =>
                    redirectToDetailPage(asset_id, DetailPageTab.WORKSHOP_ACTIONS)
                }
                onClickMaintenanceSection={(asset_id: string) =>
                    redirectToDetailPage(asset_id, DetailPageTab.MAINTENANCE_AND_FILL_LEVELS)
                }
                onClickBrakeLiningsSection={(asset_id: string) =>
                    redirectToDetailPage(asset_id, DetailPageTab.WEAR_AND_TEAR)
                }
                onClickWarningMessagesSection={(assetId: string) =>
                    redirectToDetailPage(assetId, DetailPageTab.WARNING_MESSAGES)
                }
                onClickDtcDm1Section={(assetId: string) => redirectToDetailPage(assetId, DetailPageTab.DTC_DM1)}
                onClickDtcDm4Section={(assetId: string) => redirectToDetailPage(assetId, DetailPageTab.DTC_DM4)}
                highlightAssetId={highlightAssetId}
                clearHighlight={clearHighlight}
                selectedDataTabsSections={selectedDataTabsSections}
            />
            {vehiclesStatusData.length > 0 && (
                <PaginationControlsComponent
                    isLoading={isLoading}
                    pageSize={pageSize}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    onPageSizeChange={changePageSize}
                    onPageNumberChange={changePageNumber}
                />
            )}
        </div>
    );
};

export default VehicleDataTabsContainer;
